import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useCrossFlow, Targets, Journeys } from '@atlassiansox/cross-flow-support';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { useTriggerCrossFlowSuccessCheck } from '@confluence/loom-utils/entry-points/useTriggerCrossFlowSuccessCheck';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';
import { getLogger } from '@confluence/logger';

import {
	type RovoLoomScriptTrigger,
	RovoLoomScriptTriggerEvent,
	useRovoLoomScriptTrigger,
	useRovoLoomScriptTriggerListener,
} from '../hooks/useRovoLoomScriptTrigger';

interface LoomScriptCrossFlowContextValue {
	openLoomCrossFlow: () => void;
}

const LoomScriptCrossFlowContext = createContext<LoomScriptCrossFlowContextValue>({
	openLoomCrossFlow: () => {},
});

const SOURCE_COMPONENT = 'readTimeDropdownLoomRecording';
const SOURCE_CONTEXT = 'confluenceReadTimeRecordAVideo';

export const useLoomScriptCrossFlowContext = () => useContext(LoomScriptCrossFlowContext);

const logger = getLogger('LoomScriptCrossFlowContext');

export const LoomScriptCrossFlowContextProvider = ({ children }: { children: React.ReactNode }) => {
	const { isSiteAdmin } = useContext(SPAViewContext);
	const crossFlow = useCrossFlow();
	const openFn = crossFlow.isEnabled ? crossFlow.apiV1.open : null;
	const { triggerCrossFlowSuccessCheck } = useTriggerCrossFlowSuccessCheck({
		sourceComponent: SOURCE_COMPONENT,
	});
	const { entryPointVariant } = useLoomEntryPointVariant();

	const { triggerRovoLoomScript } = useRovoLoomScriptTrigger();

	useRovoLoomScriptTriggerListener(
		{
			event: RovoLoomScriptTriggerEvent.CROSS_FLOW_SUCCESS,
			entryPointId: SOURCE_COMPONENT,
		},
		entryPointVariant,
		true,
	);

	const openLoomCrossFlow = useCallback(async () => {
		if (!openFn) return;
		try {
			const { success } = await openFn({
				journey: Journeys.GET_STARTED,
				sourceComponent: SOURCE_COMPONENT,
				sourceContext: SOURCE_CONTEXT,
				targetProduct: Targets.LOOM,
				v1: true,
			});

			if (isSiteAdmin && success) {
				logger.debug`[ReadTimeDropdown] Cross-flow success, setting auto-trigger flag`;
				const trigger: RovoLoomScriptTrigger = {
					event: RovoLoomScriptTriggerEvent.CROSS_FLOW_SUCCESS,
					entryPointId: SOURCE_COMPONENT,
				};

				await triggerRovoLoomScript(trigger);

				triggerCrossFlowSuccessCheck({
					onCrossflowFailure: () => {
						logger.debug`[ReadTimeDropdown] Cross-flow check failed, resetting flag`;
						// don't open rovo if cross-flow fails
					},
				});
			}
		} catch (error: any) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.GROWTH_MULTIPLIERS,
			});
		}
	}, [isSiteAdmin, triggerCrossFlowSuccessCheck, openFn, triggerRovoLoomScript]);

	const value: LoomScriptCrossFlowContextValue = useMemo(() => {
		return {
			openLoomCrossFlow,
		};
	}, [openLoomCrossFlow]);

	return (
		<LoomScriptCrossFlowContext.Provider value={value}>
			{children}
		</LoomScriptCrossFlowContext.Provider>
	);
};
